import { useMemo, useEffect, useState } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { useVacationBalanceQuery } from '@/features/Vacations/hooks/data/useVacationBalanceQuery';
import { previewVacationBalance } from '../vacationBalancePreview';
import type { VacationBalance } from '@/features/Vacations/models/vacation.types';

interface UseVacationBalancePreviewResult {
  balance: VacationBalance | null;
  isPreview: boolean;
  canPreview: boolean;
  isSyncing: boolean;
}

export function useVacationBalancePreview(collaboratorId?: string): UseVacationBalancePreviewResult {
  const { watch } = useFormContext();
  const { isDirty } = useFormState({ name: ['vacationPeriods', 'startDate'] });
  const formData = watch();
  const [shouldPoll, setShouldPoll] = useState(false);

  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const { balance: serverBalance, isPolling } = useVacationBalanceQuery(collaboratorId, {
    poll: shouldPoll,
  });

  const actualPreview = useMemo(() => {
    return previewVacationBalance(
      serverBalance ?? null,
      {
        vacationPeriods: formData.vacationPeriods || [],
        startDate: formData.startDate || '',
      },
      todayIso
    );
  }, [serverBalance, formData.vacationPeriods, formData.startDate, todayIso]);

  useEffect(() => {
    setShouldPoll(actualPreview.isPreview && !isDirty);
  }, [actualPreview.isPreview, isDirty]);

  const isPreview = actualPreview.isPreview && isDirty;
  const isSyncing = !isDirty && actualPreview.isPreview && isPolling;

  return {
    balance: actualPreview.balance,
    isPreview,
    canPreview: actualPreview.canPreview,
    isSyncing,
  };
}
